import React, { useEffect, useState } from "react";
import { Calendar, Mail } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  updateCustomer,
  updateCustomerStatus,
} from "../../actions/CustomerAction";
import { COUNTRYCODE_LIST } from "../../constants/CountryCode";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { formatDate } from "../../utils/DateFormat";
import DropdownBox from "../layout/DropdownBox";

const EditCustomerDetails = ({ customer, closeModal, refreshData }) => {
  const [customerStatus, setCustomerStatus] = useState(customer.status);
  const [customerMobileNumber, setCustomerMobileNumber] = useState(
    customer.mobileNumber || "",
  );
  const [customerEmail, setCustomerEmail] = useState(customer.email || "");
  const [phoneCode, setPhoneCode] = useState("32");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    handlePhoneNumber();
  }, []);

  const handleCancel = () => {
    closeModal();
    if (customerStatus !== customer.status) {
      refreshData();
    }
  };

  const isValidPhoneNumber = (phone) => {
    try {
      if (!phone.startsWith("+")) {
        phone = "+" + phone;
      }
      const parsedNumber = parsePhoneNumberFromString(phone);
      return parsedNumber?.isValid() || false;
    } catch (error) {
      return false;
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handlePhoneNumber = () => {
    try {
      let formattedNumber = customerMobileNumber;
      if (formattedNumber && !formattedNumber.startsWith("+")) {
        formattedNumber = "+" + formattedNumber;
      }
      const parsedNumber = parsePhoneNumberFromString(formattedNumber);
      if (parsedNumber) {
        setPhoneCode("+" + parsedNumber.countryCallingCode);
        setPhoneNumber(parsedNumber.nationalNumber);
      }
    } catch (error) {
      console.error("Error parsing mobile number:", error);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setPhoneNumber(value);
      updateFullMobileNumber(phoneCode, value);
      if (!isValidPhoneNumber(`${phoneCode}${value}`)) {
        setPhoneError("Please enter a valid mobile number");
      } else {
        setPhoneError("");
      }
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setCustomerEmail(value);

    if (value === "") {
      setEmailError("");
    } else if (!isValidEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneCodeChange = (e) => {
    const value = e.target.value;
    setPhoneCode(value);
    updateFullMobileNumber(value, phoneNumber);
  };

  const updateFullMobileNumber = (code, number) => {
    const fullNumber = `${code.replace("+", "")}${number}`.trim();
    setCustomerMobileNumber(fullNumber);
  };

  const handleStatusChange = async (newStatus) => {
    const formData = new FormData();
    formData.append("customerId", customer._id);
    formData.append("status", newStatus);
    try {
      setIsLoading(true);
      const response = await updateCustomerStatus(
        formData.get("customerId"),
        formData.get("status"),
      );
      if (response.success) {
        toast.success(
          response.message || "Customer status updated successfully!",
        );
      } else {
        toast.error(response?.message || "Failed to update customer status.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCustomer = async () => {
    setEmailError("");

    // Validate email
    if (customerEmail !== "" && !isValidEmail(customerEmail)) {
      setEmailError("Please enter a valid email address");
      toast.error("Please enter a valid email address.");
      return;
    }

    const formData = new FormData();
    let hasChanges = false;
    let hasStatusChange = false;

    if (customerStatus !== customer.status) {
      hasStatusChange = true;
    }

    // Handle email changes
    const normalizedCustomerEmail = customer.email || "";
    if (customerEmail !== normalizedCustomerEmail) {
      formData.append("email", customerEmail);
      hasChanges = true;
    }

    if (!hasChanges && !hasStatusChange) {
      toast.info("No changes detected.");
      return;
    }

    if (hasStatusChange) {
      await handleStatusChange(customerStatus);
      if (!hasChanges) {
        setTimeout(() => {
          closeModal();
          refreshData();
        }, 2000);
        return;
      }
    }

    formData.append("customerId", customer._id);

    try {
      setIsLoading(true);
      const response = await updateCustomer(formData);
      if (response.success) {
        toast.success(
          response?.message || "Customer details updated successfully!",
        );
        setTimeout(() => {
          closeModal();
          refreshData();
        }, 2000);
      } else {
        toast.error(response?.message || "Failed to update customer details.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 sm:p-6 rounded-[20px] w-full max-w-[800px] shadow-lg mx-2 flex flex-col max-h-[80vh] overflow-y-auto text-xs sm:text-base">
        <div className="flex flex-col sm:flex-row sm:justify-between mb-4">
          <div className="justify-start">
            <h2 className="text-2xl sm:text-3xl font-bold">
              Edit Customer Detail
            </h2>
            <p className="text-text2 font-normal text-sm pt-1">
              Last updated: {formatDate(customer.updatedAt)}
            </p>
          </div>
          <div className="hidden sm:flex gap-3 my-1">
            <button
              onClick={handleCancel}
              className="secondary_button inactive w-24"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleEditCustomer}
              className={`secondary_button w-24 ${isLoading ? "opacity-50 cursor-not-allowed" : "active"}`}
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Customer Id Input */}
          <div className="mb-2 sm:mb-4 space-y-0.5 sm:space-y-1">
            <label className="md:font-medium font-normal text-sm leading-5 text-text3">
              Number
            </label>
            <input
              type="text"
              placeholder="Enter customer name"
              className="secondary_input text-text1 w-full p-2 border border-[#D1D5DB] rounded cursor-not-allowed"
              disabled
              value={customer.uid}
            />
          </div>
          {/* Date Joined */}
          <div className="mb-4 space-y-1">
            <label className="md:font-medium font-normal text-sm leading-5 text-text3">
              Date Joined
            </label>
            <div className="flex items-center border border-gray-300 rounded px-3 bg-gray-50">
              <Calendar className="text-gray-500 mr-3 h-5 w-5" />
              <input
                type="text"
                className="secondary_input text-text1 w-full bg-transparent outline-none cursor-not-allowed border-l border-[#D1D5DB]"
                disabled
                value={formatDate(customer.createdAt) || ""}
              />
            </div>
          </div>

          {/* Mobile Number with Country Code */}
          <div className="mb-4 space-y-1">
            <label className="md:font-medium font-normal text-sm leading-5 text-text3">
              Mobile Number
            </label>
            <div className="flex items-center border border-gray-300 pl-3 rounded bg-gray-50">
              <select
                className="pr-1 mr-1 border-gray-300 focus:outline-none cursor-not-allowed bg-gray-50"
                value={phoneCode}
                onChange={handlePhoneCodeChange}
                disabled
              >
                {COUNTRYCODE_LIST.map((code, index) => (
                  <option key={index} value={code.dialCode}>
                    {code.dialCode}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Enter mobile number"
                className="secondary_input text-text1 w-full border-l rounded-md rounded-l-none border-gray-300 bg-gray-50 focus:outline-none focus:ring-0 cursor-not-allowed"
                value={phoneNumber}
                onChange={handlePhoneChange}
                disabled
              />
            </div>
            {phoneError && (
              <p className="text-sm text-required mt-1">{phoneError}</p>
            )}
          </div>
          {/* Customer Email Address Input */}
          <div className="mb-4 space-y-1">
            <label className="md:font-medium font-normal text-sm leading-5 text-text3">
              Email Address
            </label>
            <div className="flex items-center border border-gray-300 pl-3 rounded bg-white">
              <Mail className="text-gray-500 mr-3" />
              <input
                type="email"
                placeholder="Enter email address"
                className="secondary_input text-text1 w-full border-l rounded-md rounded-l-none border-gray-300 bg-white focus:outline-none focus:ring-0"
                value={customerEmail}
                onChange={handleEmailChange}
                disabled={isLoading}
              />
            </div>
            {emailError && (
              <p className="text-sm text-required mt-1">{emailError}</p>
            )}
          </div>
        </div>
        {/* Status Dropdown */}
        <div className="flex flex-col sm:flex-row justify-between items-center bg-gray-50 p-2 rounded-md sm:mb-16">
          <div>
            <p className="font-medium text-[14px]">Update status</p>
            <p className="text-sm text-gray-500">
              This will affect the visibility of the specific brand.
            </p>
          </div>
          <div className="w-full sm:w-40 mt-2 sm:mt-0">
            <DropdownBox
              optionList={["ACTIVE", "SUSPENDED"]}
              styleName="rounded-md text-sm font-normal text-text1 uppercase focus:outline-none focus:ring-0 w-full"
              value={customerStatus}
              handleChange={(selectedValue) => setCustomerStatus(selectedValue)}
            />
          </div>
        </div>
        {/* Mobile Buttons */}
        <div className="sm:hidden flex justify-end gap-3 mt-4">
          <button
            onClick={handleCancel}
            className="secondary_button inactive w-24"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleEditCustomer}
            className={`secondary_button w-24 ${isLoading ? "opacity-50 cursor-not-allowed" : "active"}`}
            disabled={isLoading}
          >
            {isLoading ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCustomerDetails;
